import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError, API } from "../lib/api";
import { GBP, GBP2, fmtKwh, fmtKwp, fmtDateGB, statusLabel, statusTone } from "../lib/format";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Sun, Leaf, TrendingUp, FileDown, CheckCircle2 } from "lucide-react";
import { useSettings, BrandLogo } from "../context/SettingsContext";

const HERO = "https://static.prod-images.emergentagent.com/jobs/eb57b103-83f0-456b-abc5-fceb1d25bc4c/images/39888e2a027ded983ba62884be38f79f50868a11dfdfb25c1b847fe4bf5dc41f.png";

export default function CustomerPortal() {
  const { token } = useParams();
  const { settings } = useSettings();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/public/portal/${token}`)
      .then(r => setData(r.data))
      .catch(e => setErr(formatApiError(e.response?.data?.detail, "Portal not found")));
  }, [token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-10 text-center">
      <div>
        <div className="font-display text-3xl font-bold">Portal unavailable</div>
        <div className="muted mt-2 text-sm">{err}</div>
      </div>
    </div>
  );
  if (!data) return <div className="p-10 muted font-mono text-xs uppercase tracking-widest">Loading your portal…</div>;

  const { project, design, quote, tasks, monitoring, progress, org, images } = data;
  const m = monitoring || {};

  return (
    <div className="min-h-screen bg-white" data-testid="customer-portal-page">
      <header className="relative h-72 overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover grayscale" />
        <div className="absolute inset-0 bg-[var(--ink)]/65" />
        <div className="relative z-10 h-full max-w-6xl mx-auto px-8 flex flex-col justify-between py-10 text-white">
          <div className="flex items-center gap-3">
            <BrandLogo size={36} />
            <div>
              <div className="font-display font-bold text-xl">{settings?.org_name || org?.org_name || "FLOW Energy App"}</div>
              <div className="label !text-white/70 !text-[10px]">Customer Portal</div>
            </div>
          </div>
          <div>
            <div className="label !text-white/70" data-testid="portal-status-label">
              <span className={`tag ${statusTone(project?.status)}`}>{statusLabel(project?.status)}</span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold leading-tight mt-3" data-testid="portal-project-name">{project?.name}</h1>
            <div className="muted text-white/80 text-sm mt-2">{project?.address} · {project?.postcode}</div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-12 space-y-12">
        {/* System design image */}
        {project?.design_image_url && (
          <section>
            <img src={project.design_image_url} alt="System design"
                 className="w-full rounded-xl border object-cover max-h-[380px]"
                 style={{ borderColor: "var(--line)" }}
                 data-testid="portal-design-image" />
          </section>
        )}

        {/* Headline KPIs */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-1 bg-[var(--surface-2)]">
          <Stat label="Your system" value={fmtKwp(design?.systemSize || 0)} sub={`${design?.numberOfModules || 0} modules`} testid="portal-kpi-system" />
          <Stat label="Live performance" value={`${m.performance_ratio_pct?.toFixed?.(1) || 0}%`}
                sub={(m.performance_ratio_pct || 0) >= 95 ? "On target" : "Tracking"}
                accent testid="portal-kpi-performance" />
          <Stat label="Savings to date" value={GBP.format(m.savings_to_date_gbp || 0)} sub="Since handover" testid="portal-kpi-savings" />
          <Stat label="CO₂ saved" value={`${((m.co2_saved_kg || 0) / 1000).toFixed(2)} t`} sub={`${((m.co2_saved_kg || 0) / 21).toFixed(0)} trees-equiv`} testid="portal-kpi-co2" />
        </section>

        {/* Performance chart */}
        <section className="swiss-card-flat p-6" data-testid="portal-performance-chart">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="label">Energy production</div>
              <div className="font-display text-xl font-semibold mt-1">Actual vs design simulation · daily kWh</div>
            </div>
            <div className="text-right text-xs">
              <div className="font-mono">{fmtKwh(m.total_actual_kwh || 0)}</div>
              <div className="muted">actual · {fmtKwh(m.total_simulated_kwh || 0)} simulated</div>
            </div>
          </div>
          {(m.samples || []).length === 0 ? (
            <div className="muted text-sm py-8 text-center">Monitoring data will appear once the system is fully commissioned.</div>
          ) : (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <AreaChart data={m.samples || []}>
                  <defs>
                    <linearGradient id="cpSim" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#A1A1AA" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#A1A1AA" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="cpAct" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" />
                  <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" />
                  <Tooltip contentStyle={{ borderRadius: 0, fontSize: 12 }} />
                  <Area dataKey="simulated_kwh" name="Simulated" stroke="#52525B" fill="url(#cpSim)" strokeWidth={1} />
                  <Area dataKey="actual_kwh" name="Actual" stroke="#F59E0B" fill="url(#cpAct)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        {/* Install progress / timeline */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-1 bg-[var(--surface-2)]">
          <div className="md:col-span-2 bg-white p-6 border border-[var(--line)]" data-testid="portal-timeline-card">
            <div className="label mb-3">Installation timeline</div>
            <div className="space-y-3">
              {(tasks || []).map(t => (
                <div key={t.id} className="flex items-center justify-between gap-3 py-2 border-b border-[var(--line)] last:border-0" data-testid={`portal-task-${t.id}`}>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 size={14} className={t.status === "done" ? "text-[var(--ok)]" : "muted-x"} />
                    <div>
                      <div className="text-sm font-medium">{t.milestone && <span className="text-[var(--signal)] mr-1">◆</span>}{t.name}</div>
                      <div className="text-[11px] muted font-mono">{fmtDateGB(t.start_date)} → {fmtDateGB(t.end_date)}</div>
                    </div>
                  </div>
                  <span className={`tag ${statusTone(t.status)}`}>{statusLabel(t.status)}</span>
                </div>
              ))}
              {!tasks?.length && <div className="muted text-sm py-4">No timeline available yet.</div>}
            </div>
          </div>
          <div className="bg-white p-6 border border-[var(--line)]" data-testid="portal-progress-card">
            <div className="label">Project completion</div>
            <div className="kpi-value mt-2">{progress?.pct || 0}%</div>
            <div className="muted text-xs">{progress?.done || 0} of {progress?.total || 0} tasks complete</div>
            <div className="h-2 bg-[var(--surface)] mt-4">
              <div className="h-full bg-[var(--signal)]" style={{ width: `${progress?.pct || 0}%` }} />
            </div>
            {project?.handover_date && (
              <div className="mt-6 text-xs">
                <div className="label">Handover</div>
                <div className="font-mono">{fmtDateGB(project.handover_date)}</div>
              </div>
            )}
            {quote && (
              <>
                <div className="dotted-divider my-5" />
                <div className="label">Your contract</div>
                <div className="font-mono text-sm mt-1">{GBP2.format(quote.total_gbp || 0)}</div>
                <div className="text-xs muted">{statusLabel(quote.status)}</div>
                {quote.pdf_url && (
                  <a
                    href={`${API.replace('/api','')}${quote.pdf_url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost mt-3 w-full justify-center"
                    data-testid="portal-quote-pdf-btn"
                  >
                    <FileDown size={13} /> Download signed quote
                  </a>
                )}
              </>
            )}
          </div>
        </section>

        {/* System spec */}
        {design?.arrays?.length > 0 ? (
          <section data-testid="portal-arrays">
            <div className="label mb-3">Your sub-arrays</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1 bg-[var(--surface-2)]">
              {design.arrays.map(a => (
                <div key={a.id} className="bg-white p-5 border border-[var(--line)]" data-testid={`portal-array-${a.id}`}>
                  <div className="label">{a.name}</div>
                  <div className="font-display text-xl font-semibold mt-1">{fmtKwp((a.numberOfModules * a.moduleWattage) / 1000)}</div>
                  <div className="muted text-xs">{a.numberOfModules} × {a.moduleModel || `${a.moduleWattage}Wp`}</div>
                  <div className="muted text-xs">{a.tilt}° tilt · {a.azimuth}° azimuth</div>
                  <div className="muted text-xs mt-1">{fmtKwh(a.annualEnergyProduction || 0)} / year</div>
                </div>
              ))}
            </div>
          </section>
        ) : (
          design && (
            <section className="swiss-card-flat p-6" data-testid="portal-system-spec">
              <div className="label mb-3">System</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 text-sm">
                <div><div className="label">Modules</div><div className="font-mono">{design.numberOfModules} × {design.moduleWattage}Wp</div></div>
                <div><div className="label">Inverter</div><div className="font-mono">{design.inverterModel || "—"}</div></div>
                <div><div className="label">Tilt / azimuth</div><div className="font-mono">{design.tilt}° / {design.azimuth}°</div></div>
                <div><div className="label">Design yield</div><div className="font-mono">{fmtKwh(design.annualEnergyProduction)}/yr</div></div>
              </div>
            </section>
          )
        )}

        {(images || []).length > 0 && (
          <section data-testid="portal-images">
            <div className="label mb-4">Your system · design renderings</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {images.map(img => (
                <a key={img.id} href={img.url} target="_blank" rel="noreferrer" className="block border border-[var(--line)] overflow-hidden hover-lift" data-testid={`portal-img-${img.id}`}>
                  <img src={img.url} alt={img.name} className="w-full h-48 object-cover" loading="lazy" />
                  <div className="text-[11px] muted px-3 py-2 bg-[var(--surface)]">{img.name}</div>
                </a>
              ))}
            </div>
          </section>
        )}

        <footer className="border-t border-[var(--line)] pt-6 text-xs muted">
          <div className="flex items-center gap-2"><Leaf size={11} /> Operated by {org?.org_name} · {org?.org_address}</div>
          <div className="mt-1">This portal is private to you. Bookmark this page for lifetime access to your installation data.</div>
        </footer>
      </main>
    </div>
  );
}

function Stat({ label, value, sub, accent, testid }) {
  return (
    <div className={`p-5 border border-[var(--line)] ${accent ? "bg-[var(--ink)] text-white" : "bg-white"}`} data-testid={testid}>
      <div className={`label ${accent ? "!text-white/70" : ""}`}>{label}</div>
      <div className={`font-display text-2xl md:text-3xl font-semibold mt-2 ${accent ? "text-white" : ""}`}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${accent ? "text-white/70" : "muted"}`}>{sub}</div>}
    </div>
  );
}
